<template>
    <div class="titulo">
        <div class="margem container titulo-gestao-entregas">
            <h2>Gestão de pacotes de trabalho</h2>
            <button type="button" class="toggle-estrategicos acao-secundaria"
                :class="{ 'toggle-estrategicos--ativo': somenteProjetosEstrategicos }"
                :aria-pressed="somenteProjetosEstrategicos"
                :title="somenteProjetosEstrategicos ? 'Exibindo somente projetos estratégicos' : 'Exibindo todos os projetos'"
                @click="somenteProjetosEstrategicos = !somenteProjetosEstrategicos">
                <i class="bi" :class="somenteProjetosEstrategicos ? 'bi-funnel-fill' : 'bi-funnel'"
                    aria-hidden="true"></i> Somente projetos estratégicos </button>
        </div>
    </div>
    <div class="margem gestao-entregas-corpo">
        <div class="bloco margem filtros-periodo">
            <div class="navegacao-semana">
                <button type="button" class="acao-secundaria btn-setas" aria-label="Semana anterior" :disabled="loading"
                    @click="semanaAnterior">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i>
                </button>
                <div class="periodo-central">
                    <h3 class="periodo-titulo">{{ labelSemanaExibida }}</h3>
                    <div class="periodo-datas">
                        <div class="periodo-campo">
                            <label for="periodo-inicio">De</label>
                            <input id="periodo-inicio" v-model="periodoInicio" type="date" :disabled="loading"
                                @change="carregarDados" />
                        </div>
                        <div class="periodo-campo">
                            <label for="periodo-fim">Até</label>
                            <input id="periodo-fim" v-model="periodoFim" type="date" :disabled="loading"
                                @change="carregarDados" />
                        </div>
                    </div>
                </div>
                <button type="button" class="acao-secundaria btn-setas" aria-label="Próxima semana" :disabled="loading"
                    @click="semanaProxima">
                    <i class="bi bi-chevron-right" aria-hidden="true"></i>
                </button>
                <button type="button" class="acao-secundaria btn-hoje-icon"
                    :class="{ 'btn-hoje-icon--ativo': estaNaSemanaAtual }" title="Semana atual"
                    aria-label="Ir para a semana atual" :disabled="loading" @click="definirSemanaAtual">
                    <i class="bi bi-calendar-day" aria-hidden="true"></i>
                </button>
            </div>
            <p class="filtros-periodo-hint alinha-centro"> Pacotes de trabalho não validados pela gestão (inclui concluídos
                aguardando validação) com fim previsto no período. </p>
            <div class="acoes-reuniao-google">
                <button v-if="!vinculoReuniaoValido" type="button" class="acao-secundaria"
                    :disabled="loading || salvando || criandoReuniao" @click="criarReuniaoGoogle">
                    <i class="bi bi-calendar-plus" aria-hidden="true"></i> {{ criandoReuniao ? 'Criando reunião…' :
                        'Criar reunião no Google Agenda' }} </button>
                <div v-else class="reuniao-vinculada-painel">
                    <span class="chip chip-vinculo-reuniao" :title="tituloReuniaoVinculada">
                        <i class="bi bi-calendar-check" aria-hidden="true"></i> {{ tituloReuniaoVinculada }} </span>
                    <a v-if="vinculoReuniao?.htmlLink" :href="vinculoReuniao.htmlLink" target="_blank"
                        class="acao-secundaria btn-reuniao-acao"> Abrir no Google </a>
                    <button type="button" class="acao-secundaria btn-reuniao-acao"
                        :disabled="salvando || excluindoReuniao" @click="desvincularReuniao"> Desvincular </button>
                    <button type="button" class="acao-secundaria btn-reuniao-acao btn-reuniao-excluir"
                        :disabled="salvando || excluindoReuniao" @click="excluirReuniaoVinculada"> {{ excluindoReuniao ?
                            'Excluindo…' : 'Excluir reunião' }} </button>
                </div>
            </div>
        </div>
        <div v-if="loading" class="loading" role="status" aria-live="polite" aria-label="Carregando pacotes de trabalho">
            <div></div>
        </div>
        <template v-else>
            <section class="secao-painel bloco margem" aria-labelledby="titulo-entregas-periodo">
                <div class="secao-cabecalho">
                    <div class="secao-cabecalho-principal">
                        <h3 id="titulo-entregas-periodo">Pacotes de trabalho no período</h3>
                        <span class="chip bg-cinza">{{ entregasExibidas.length }}</span>
                    </div>
                    <div class="legenda-estados" role="group" aria-label="Legenda dos estados do pacote de trabalho">
                        <span class="legenda-estados-label">Estados:</span>
                        <LegendaEstadoSprint v-for="estado in estadosReferencia" :key="estado" :estado="estado"
                            contexto="entrega" referencia />
                    </div>
                </div>
                <p v-if="entregasExibidas.length === 0" class="estado-vazio alinha-centro">
                    <span class="chip bg-cinza vazio">{{ mensagemVazioPeriodo }}</span>
                </p>
                <EntregaGestaoTabela v-else :entregas="entregasExibidas" aria-label="Pacotes de trabalho no período"
                    :selecionados="entregasSelecionadas" :repactuacoes-pendentes="repactuacoesPendentes"
                    :permitir-acoes-gestao="isAuditor" mostrar-coluna-fixar :entregas-fixadas="entregasFixadas"
                    @abrir="abrirEntrega" @repactuar="abrirRepactuacao" @toggle-validar="toggleSelecionar"
                    @toggle-fixar="toggleFixarEntrega" />
                <footer v-if="mostrarRodape && isAuditor">
                    <div class="rodape-botoes submit direita">
                        <p v-if="temAlteracoesPendentes" class="rodape-selecao" role="status"> {{
                            textoAlteracoesPendentes }} </p>
                        <button type="button" class="acao-secundaria" :disabled="salvando || !temAlteracoesPendentes"
                            @click="descartarAlteracoesPendentes"> Descartar </button>
                        <button type="button" :disabled="salvando || !temAlteracoesPendentes" @click="salvarAlteracoes">
                            {{ salvando ? 'Salvando…' : 'Salvar alterações' }} </button>
                    </div>
                </footer>
            </section>
            <section class="secao-painel secao-fixadas bloco margem" aria-labelledby="titulo-fixadas">
                <div class="secao-cabecalho">
                    <div class="secao-cabecalho-principal">
                        <h3 id="titulo-fixadas">Pacotes de trabalho fixados</h3>
                        <span class="chip bg-cinza">{{ fixadasExibidas.length }}</span>
                    </div>
                </div>
                <p class="secao-descricao"> Pacotes de trabalho que você fixou para acompanhar sempre (independente do período
                    selecionado). </p>
                <p v-if="fixadasExibidas.length === 0" class="estado-vazio alinha-centro">
                    <span class="chip bg-cinza vazio">{{ mensagemVazioFixadas }}</span>
                </p>
                <EntregaGestaoTabela v-else :entregas="fixadasExibidas" ocultar-coluna-validar
                    :permitir-acoes-gestao="isAuditor" mostrar-coluna-fixar :entregas-fixadas="entregasFixadas"
                    aria-label="Pacotes de trabalho fixados" :selecionados="entregasSelecionadas"
                    :repactuacoes-pendentes="repactuacoesPendentes" @abrir="abrirEntrega" @repactuar="abrirRepactuacao"
                    @toggle-fixar="toggleFixarEntrega" />
            </section>
            <section class="secao-painel secao-atrasadas bloco margem" aria-labelledby="titulo-atrasadas">
                <div class="secao-cabecalho">
                    <div class="secao-cabecalho-principal">
                        <h3 id="titulo-atrasadas">Pacotes de trabalho atrasados</h3>
                        <span class="chip bg-cinza">{{ atrasadasExibidas.length }}</span>
                    </div>
                </div>
                <p class="secao-descricao"> Pacotes de trabalho ativos com fim previsto anterior a hoje (independente do período
                    selecionado). </p>
                <p v-if="atrasadasExibidas.length === 0" class="estado-vazio alinha-centro">
                    <span class="chip bg-cinza vazio">{{ mensagemVazioAtrasadas }}</span>
                </p>
                <EntregaGestaoTabela v-else :entregas="atrasadasExibidas" ocultar-coluna-validar
                    :permitir-acoes-gestao="isAuditor" aria-label="Pacotes de trabalho atrasados"
                    :selecionados="entregasSelecionadas" :repactuacoes-pendentes="repactuacoesPendentes"
                    @abrir="abrirEntrega" @repactuar="abrirRepactuacao" />
            </section>
            <section class="secao-painel secao-sem-fim bloco margem" aria-labelledby="titulo-sem-fim">
                <div class="secao-cabecalho">
                    <div class="secao-cabecalho-principal">
                        <h3 id="titulo-sem-fim">Sem fim previsto</h3>
                        <span class="chip bg-cinza">{{ semFimExibidas.length }}</span>
                    </div>
                </div>
                <p class="secao-descricao"> Pacotes de trabalho ativos sem data de fim previsto (independente do período
                    selecionado). </p>
                <p v-if="semFimExibidas.length === 0" class="estado-vazio alinha-centro">
                    <span class="chip bg-cinza vazio">{{ mensagemVazioSemFim }}</span>
                </p>
                <EntregaGestaoTabela v-else :entregas="semFimExibidas" modo-sem-fim :permitir-acoes-gestao="isAuditor"
                    aria-label="Pacotes de trabalho sem fim previsto" :selecionados="entregasSelecionadas"
                    :repactuacoes-pendentes="repactuacoesPendentes" @abrir="abrirEntrega"
                    @repactuar="abrirRepactuacao" />
            </section>
        </template>
        <ModalRepactuarEntrega :mostrar="modalRepactuarAberto" :entrega-nome="entregaRepactuacao?.entrega_nome"
            :fim-previsto-atual="entregaRepactuacao?.fim_previsto_inicial"
            :fim-previsto-referencia="entregaRepactuacao?.fim_previsto_referencia"
            :observacao-repactuacao-historico="entregaRepactuacao?.observacao_repactuacao_historico || ''"
            :observacao-repactuacao-linha-pendente="entregaRepactuacao?.observacao_repactuacao_linha_pendente || ''"
            @fechar="fecharRepactuacao" @salvar="confirmarRepactuacaoPendente" />
        <ModalVincularReuniaoGoogle :mostrar="modalVincularReuniaoAberto" :periodo-inicio="periodoInicio"
            :periodo-fim="periodoFim" @fechar="onModalVinculoFechar" @vinculado="onModalVinculoVinculado"
            @pular-anexo="onModalVinculoPularAnexo" />
        <button v-if="!loading && isAuditor" type="button" class="acao-secundaria btn-modo-apresentacao"
            title="Abrir modo apresentação" @click="modoApresentacaoAberto = true">
            <i class="bi bi-easel" aria-hidden="true"></i> Modo apresentação </button>
        <ModoApresentacaoGestaoEntregas :aberto="modoApresentacaoAberto" :id-usuario="idUsuario"
            :periodo-inicio="periodoInicio" :periodo-fim="periodoFim" :entregas-exibidas="entregasExibidas"
            :fixadas-exibidas="fixadasExibidas" :atrasadas-exibidas="atrasadasExibidas"
            :sem-fim-exibidas="semFimExibidas" @fechar="modoApresentacaoAberto = false" />
    </div>
</template>
<script>
import { useToast } from 'vue-toastification'
import { sso } from 'roboflex-thalamus-sso-lib'
import serviceProjetos from '@/services/serviceProjetos'
import serviceGoogleCalendar from '@/services/serviceGoogleCalendar'
import {
    montarDadosRelatorioGestaoEntregas,
    gerarRelatorioGestaoEntregasPdfBlob,
} from '@/utils/relatorioGestaoEntregasPdf'
import {
    lerVinculoReuniao,
    limparVinculoReuniao,
    montarPayloadCriarReuniaoGestao,
    salvarVinculoReuniao,
    vinculoValidoParaPeriodo,
} from '@/utils/googleCalendarGestaoEntregas'
import {
    getSegundaASextaSemanaAtual,
    compararEntregasGestao,
    compararEntregasSemFimGestao,
    carregarEntregasFixadas,
    salvarEntregasFixadas,
    extrairLinhaNovaRepactuacao,
    deslocarSemana,
    isSemanaAtual,
    labelPeriodoSemana,
    ESTADOS_ENTREGA_REFERENCIA,
    filtrarEntregasGestao,
} from '@/utils/sprintGestao'
import { getPermissao } from '@/services/permissao-service'
import { useApiError } from '@/composables/useApiError'
import LegendaEstadoSprint from '@/components/Gestão/LegendaEstadoSprint.vue'
import EntregaGestaoTabela from '@/components/Gestão/EntregaGestaoTabela.vue'
import ModalRepactuarEntrega from '@/components/Gestão/ModalRepactuarEntrega.vue'
import ModalVincularReuniaoGoogle from '@/components/Gestão/ModalVincularReuniaoGoogle.vue'
import ModoApresentacaoGestaoEntregas from '@/components/Gestão/Apresentacao/ModoApresentacaoGestaoEntregas.vue'

export default {
    name: 'GestaoDeEntregas',

    components: {
        LegendaEstadoSprint,
        EntregaGestaoTabela,
        ModalRepactuarEntrega,
        ModalVincularReuniaoGoogle,
        ModoApresentacaoGestaoEntregas,
    },

    setup() {
        const { handleError } = useApiError()
        const toast = useToast()
        return { handleApiError: handleError, toast }
    },

    data() {
        const { inicio, fim } = getSegundaASextaSemanaAtual()
        const usuario = sso.getUsuarioLogado?.() || null
        const idUsuario = usuario?.id ?? null
        return {
            loading: true,
            salvando: false,
            idUsuario,
            isAuditor: false,
            periodoInicio: inicio,
            periodoFim: fim,
            entregas: [],
            entregasSemFimPrevisto: [],
            entregasAtrasadas: [],
            entregasFixadasLista: [],
            entregasFixadas: carregarEntregasFixadas(idUsuario),
            entregasSelecionadas: [],
            estadosReferencia: ESTADOS_ENTREGA_REFERENCIA,
            modalRepactuarAberto: false,
            entregaRepactuacao: null,
            repactuacoesPendentes: {},
            somenteProjetosEstrategicos: true,
            modalVincularReuniaoAberto: false,
            resolverModalVinculoSalvar: null,
            vinculoReuniao: null,
            criandoReuniao: false,
            excluindoReuniao: false,
            modoApresentacaoAberto: false,
        }
    },

    computed: {
        opcoesFiltroEntregas() {
            return {
                somenteEstrategico: this.somenteProjetosEstrategicos,
                idUsuario: this.idUsuario,
                isAuditor: this.isAuditor,
            }
        },

        entregasExibidas() {
            return filtrarEntregasGestao(this.entregas, this.opcoesFiltroEntregas)
                .sort(compararEntregasGestao)
        },

        semFimExibidas() {
            return filtrarEntregasGestao(this.entregasSemFimPrevisto, this.opcoesFiltroEntregas)
                .sort(compararEntregasSemFimGestao)
        },

        atrasadasExibidas() {
            return filtrarEntregasGestao(this.entregasAtrasadas, this.opcoesFiltroEntregas)
                .sort(compararEntregasGestao)
        },

        fixadasExibidas() {
            return filtrarEntregasGestao(this.entregasFixadasLista, {
                ...this.opcoesFiltroEntregas,
                incluirStatusExcluidos: true,
            }).sort(compararEntregasSemFimGestao)
        },

        labelSemanaExibida() {
            return labelPeriodoSemana(this.periodoInicio, this.periodoFim)
        },

        estaNaSemanaAtual() {
            return isSemanaAtual(this.periodoInicio, this.periodoFim)
        },

        idsRepactuacoesPendentes() {
            return Object.keys(this.repactuacoesPendentes).map(Number)
        },

        temAlteracoesPendentes() {
            return this.entregasSelecionadas.length > 0 || this.idsRepactuacoesPendentes.length > 0
        },

        mostrarRodape() {
            return this.entregasExibidas.length > 0
                || this.fixadasExibidas.length > 0
                || this.atrasadasExibidas.length > 0
                || this.semFimExibidas.length > 0
        },

        textoAlteracoesPendentes() {
            const partes = []
            if (this.idsRepactuacoesPendentes.length) {
                const n = this.idsRepactuacoesPendentes.length
                partes.push(`${n} repactuação(ões)`)
            }
            if (this.entregasSelecionadas.length) {
                partes.push(`${this.entregasSelecionadas.length} para validar`)
            }
            return partes.join(' · ')
        },

        mensagemVazioPeriodo() {
            return this.somenteProjetosEstrategicos
                ? 'Nenhum pacote de trabalho de projeto estratégico com fim previsto neste período.'
                : 'Nenhum pacote de trabalho com fim previsto neste período.'
        },

        mensagemVazioSemFim() {
            return this.somenteProjetosEstrategicos
                ? 'Nenhum pacote de trabalho de projeto estratégico sem fim previsto.'
                : 'Nenhum pacote de trabalho sem fim previsto.'
        },

        mensagemVazioAtrasadas() {
            return this.somenteProjetosEstrategicos
                ? 'Nenhum pacote de trabalho de projeto estratégico atrasado.'
                : 'Nenhum pacote de trabalho atrasado.'
        },

        mensagemVazioFixadas() {
            return this.somenteProjetosEstrategicos
                ? 'Nenhum pacote de trabalho de projeto estratégico fixado. Use o ícone de alfinete em Pacotes de trabalho no período.'
                : 'Nenhum pacote de trabalho fixado. Use o ícone de alfinete em Pacotes de trabalho no período.'
        },

        vinculoReuniaoValido() {
            return vinculoValidoParaPeriodo(this.periodoInicio, this.periodoFim, this.vinculoReuniao)
        },

        tituloReuniaoVinculada() {
            const titulo = this.vinculoReuniao?.summary
            if (titulo) return titulo
            return 'Reunião vinculada a este período'
        },
    },

    async mounted() {
        await this.carregarPermissoes()
        this.carregarDados()
        this.sincronizarVinculoReuniao()
        window.addEventListener('beforeunload', this.avisoSaidaComPendencias)
    },

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.avisoSaidaComPendencias)
    },

    beforeRouteLeave(to, from, next) {
        if (!this.temAlteracoesPendentes) {
            next()
            return
        }
        const sair = window.confirm(
            'Há alterações não salvas (repactuação ou validação). Deseja sair sem salvar?',
        )
        next(sair)
    },

    methods: {
        async carregarPermissoes() {
            try {
                const funcionalidades = await getPermissao()
                this.isAuditor = Array.isArray(funcionalidades) && funcionalidades.includes(116)
            } catch (error) {
                this.handleApiError(error)
            }
        },

        avisoSaidaComPendencias(event) {
            if (!this.temAlteracoesPendentes) return
            event.preventDefault()
            event.returnValue = ''
        },

        definirSemanaAtual() {
            const { inicio, fim } = getSegundaASextaSemanaAtual()
            this.periodoInicio = inicio
            this.periodoFim = fim
            this.carregarDados()
        },

        semanaAnterior() {
            const { inicio, fim } = deslocarSemana(this.periodoInicio, this.periodoFim, -1)
            this.periodoInicio = inicio
            this.periodoFim = fim
            this.carregarDados()
        },

        semanaProxima() {
            const { inicio, fim } = deslocarSemana(this.periodoInicio, this.periodoFim, 1)
            this.periodoInicio = inicio
            this.periodoFim = fim
            this.carregarDados()
        },

        toggleSelecionar(entrega) {
            const id = Number(entrega.entrega_id)
            if (this.repactuacoesPendentes[id]) return

            const atual = [...this.entregasSelecionadas]
            const idx = atual.indexOf(id)
            if (idx >= 0) {
                atual.splice(idx, 1)
            } else {
                atual.push(id)
            }
            this.entregasSelecionadas = atual
        },

        toggleFixarEntrega(entrega) {
            const id = Number(entrega.entrega_id)
            const atual = [...this.entregasFixadas]
            const idx = atual.indexOf(id)
            if (idx >= 0) {
                atual.splice(idx, 1)
            } else {
                atual.push(id)
            }
            this.entregasFixadas = atual
            salvarEntregasFixadas(this.idUsuario, atual)
            this.carregarDados()
        },

        sincronizarEntregasFixadasRetornadas(listaFixadas) {
            const idsRetornados = (listaFixadas || []).map((e) => Number(e.entrega_id))
            if (!this.entregasFixadas.length) return

            const idsValidos = this.entregasFixadas.filter((id) => idsRetornados.includes(id))
            if (idsValidos.length === this.entregasFixadas.length) return

            this.entregasFixadas = idsValidos
            salvarEntregasFixadas(this.idUsuario, idsValidos)
        },

        abrirRepactuacao(entrega) {
            const id = Number(entrega.entrega_id)
            const pendente = this.repactuacoesPendentes[id]
            const historico = entrega.observacao_repactuacao ?? ''
            const linhaPendente = pendente?.observacao_repactuacao
                ? extrairLinhaNovaRepactuacao(historico, pendente.observacao_repactuacao)
                : ''

            this.entregaRepactuacao = {
                ...entrega,
                fim_previsto_inicial: pendente?.dtFim ?? entrega.fim_previsto,
                fim_previsto_referencia: entrega.fim_previsto,
                observacao_repactuacao_historico: historico,
                observacao_repactuacao_linha_pendente: linhaPendente,
            }
            this.modalRepactuarAberto = true
        },

        fecharRepactuacao() {
            this.modalRepactuarAberto = false
            this.entregaRepactuacao = null
        },

        confirmarRepactuacaoPendente({ dtFim, observacao_repactuacao }) {
            if (!this.entregaRepactuacao) return

            const entregaId = Number(this.entregaRepactuacao.entrega_id)
            this.repactuacoesPendentes = {
                ...this.repactuacoesPendentes,
                [entregaId]: { dtFim, observacao_repactuacao },
            }

            this.entregasSelecionadas = this.entregasSelecionadas.filter((id) => id !== entregaId)

            this.fecharRepactuacao()
        },

        descartarAlteracoesPendentes() {
            if (!this.temAlteracoesPendentes) return
            if (!window.confirm('Descartar repactuações e seleções para validar?')) return
            this.repactuacoesPendentes = {}
            this.entregasSelecionadas = []
        },

        sincronizarVinculoReuniao() {
            this.vinculoReuniao = lerVinculoReuniao()
        },

        async criarReuniaoGoogle() {
            this.criandoReuniao = true
            try {
                const payload = montarPayloadCriarReuniaoGestao({
                    periodoInicio: this.periodoInicio,
                    periodoFim: this.periodoFim,
                    entregas: this.entregasExibidas,
                })
                const data = await serviceGoogleCalendar.criarReuniaoGestaoEntregas(payload)
                salvarVinculoReuniao({
                    reuniaoGoogleId: data?.reuniao_google_id,
                    googleEventId: data?.google_event_id || null,
                    htmlLink: data?.html_link || null,
                    summary: data?.summary || payload.summary,
                    periodoInicio: this.periodoInicio,
                    periodoFim: this.periodoFim,
                    criadaPelaTela: true,
                })
                this.sincronizarVinculoReuniao()
                this.toast.success('Reunião criada e vinculada automaticamente.')
            } catch (error) {
                const code = error?.response?.data?.code
                if (code === 'google_not_connected') {
                    this.toast.warning('Conecte sua conta Google em Reuniões Google e tente novamente.')
                    return
                }
                this.handleApiError(error)
            } finally {
                this.criandoReuniao = false
            }
        },

        desvincularReuniao() {
            if (!this.vinculoReuniaoValido) return
            limparVinculoReuniao()
            this.sincronizarVinculoReuniao()
            this.toast.info('Vínculo removido. A reunião permanece no Google Agenda.')
        },

        async excluirReuniaoVinculada() {
            if (!this.vinculoReuniaoValido) return
            const msg = this.vinculoReuniao?.criadaPelaTela
                ? 'Excluir a reunião do Google Agenda e remover o vínculo desta tela?'
                : 'Excluir a reunião vinculada do Google Agenda e remover o vínculo?'
            if (!window.confirm(msg)) return

            this.excluindoReuniao = true
            try {
                await serviceGoogleCalendar.excluirReuniaoGestaoEntregas(this.vinculoReuniao.reuniaoGoogleId)
                limparVinculoReuniao()
                this.sincronizarVinculoReuniao()
                this.toast.success('Reunião excluída e vínculo removido.')
            } catch (error) {
                const code = error?.response?.data?.code
                if (code === 'google_not_connected') {
                    this.toast.warning('Conta Google não conectada. O vínculo local foi mantido.')
                    return
                }
                this.handleApiError(error)
            } finally {
                this.excluindoReuniao = false
            }
        },

        abrirModalAnexarPdfReuniao() {
            this.modalVincularReuniaoAberto = true
        },

        onModalVinculoFechar() {
            this.modalVincularReuniaoAberto = false
            const resolver = this.resolverModalVinculoSalvar
            this.resolverModalVinculoSalvar = null
            if (resolver) resolver('cancelado')
        },

        onModalVinculoVinculado() {
            this.sincronizarVinculoReuniao()
            this.modalVincularReuniaoAberto = false
            const resolver = this.resolverModalVinculoSalvar
            this.resolverModalVinculoSalvar = null
            if (resolver) resolver('vinculado')
        },

        onModalVinculoPularAnexo() {
            this.modalVincularReuniaoAberto = false
            const resolver = this.resolverModalVinculoSalvar
            this.resolverModalVinculoSalvar = null
            if (resolver) resolver('pular')
        },

        aguardarDecisaoAnexoNoSalvar() {
            this.abrirModalAnexarPdfReuniao()
            return new Promise((resolve) => {
                this.resolverModalVinculoSalvar = resolve
            })
        },

        baixarBlob(blob, filename) {
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = filename
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            URL.revokeObjectURL(url)
        },

        async tentarAnexarPdfReuniao(file) {
            if (!this.vinculoReuniaoValido || !this.vinculoReuniao?.reuniaoGoogleId) {
                return false
            }
            try {
                await serviceGoogleCalendar.anexarPdfReuniao(this.vinculoReuniao.reuniaoGoogleId, file)
                limparVinculoReuniao()
                this.sincronizarVinculoReuniao()
                return true
            } catch (error) {
                this.handleApiError(error)
                this.toast.warning('As alterações foram salvas, mas não foi possível anexar o PDF na reunião.')
                return false
            }
        },

        async carregarDados() {
            if (!this.periodoInicio || !this.periodoFim) return

            if (this.periodoInicio > this.periodoFim) {
                this.toast.warning('A data inicial deve ser anterior ou igual à data final.')
                return
            }

            this.loading = true
            try {
                const data = await serviceProjetos.listarSprintsGestao({
                    dtInicio: this.periodoInicio,
                    dtFim: this.periodoFim,
                    entregas_fixadas: this.entregasFixadas,
                })

                const pendentesAnteriores = { ...this.repactuacoesPendentes }
                this.entregas = data.entregas || []
                this.entregasSemFimPrevisto = data.sem_fim_previsto || []
                this.entregasAtrasadas = data.atrasadas || []
                this.entregasFixadasLista = data.fixadas || []
                this.sincronizarEntregasFixadasRetornadas(this.entregasFixadasLista)

                const idsVisiveis = new Set([
                    ...this.entregas.map((e) => Number(e.entrega_id)),
                    ...this.entregasSemFimPrevisto.map((e) => Number(e.entrega_id)),
                    ...this.entregasAtrasadas.map((e) => Number(e.entrega_id)),
                    ...this.entregasFixadasLista.map((e) => Number(e.entrega_id)),
                ])

                this.entregasSelecionadas = this.entregasSelecionadas.filter((id) => idsVisiveis.has(id))
                this.repactuacoesPendentes = Object.fromEntries(
                    Object.entries(pendentesAnteriores).filter(([id]) => idsVisiveis.has(Number(id))),
                )
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.loading = false
            }
        },

        async salvarAlteracoes() {
            if (!this.temAlteracoesPendentes) return

            const snapshotRelatorio = montarDadosRelatorioGestaoEntregas({
                entregas: this.entregas,
                entregasSemFimPrevisto: this.entregasSemFimPrevisto,
                entregasAtrasadas: this.entregasAtrasadas,
                entregasFixadas: this.entregasFixadasLista,
                idsValidados: [...this.entregasSelecionadas],
                repactuacoesPendentes: { ...this.repactuacoesPendentes },
            })

            const temAlgoParaRelatorio = snapshotRelatorio.validacoes.length > 0
                || snapshotRelatorio.repactuacoes.length > 0

            this.salvando = true
            try {
                await Promise.all(
                    this.idsRepactuacoesPendentes.map((entregaId) => {
                        const dados = this.repactuacoesPendentes[entregaId]
                        return serviceProjetos.atualizarEntrega({
                            entrega_id: entregaId,
                            dtFim: dados.dtFim,
                            observacao_repactuacao: dados.observacao_repactuacao,
                        })
                    }),
                )

                if (this.entregasSelecionadas.length) {
                    await serviceProjetos.validarEntregasGestao(this.entregasSelecionadas)
                }

                this.repactuacoesPendentes = {}
                this.entregasSelecionadas = []
                await this.carregarDados()

                if (temAlgoParaRelatorio) {
                    const usuario = sso.getUsuarioLogado?.() || null
                    const relatorio = gerarRelatorioGestaoEntregasPdfBlob({
                        ...snapshotRelatorio,
                        periodoInicio: this.periodoInicio,
                        periodoFim: this.periodoFim,
                        usuarioNome: usuario?.name || usuario?.nome || '',
                    })
                    const arquivoPdf = new File([relatorio.blob], relatorio.filename, { type: 'application/pdf' })

                    let anexado = false
                    if (this.vinculoReuniaoValido) {
                        anexado = await this.tentarAnexarPdfReuniao(arquivoPdf)
                    } else {
                        const decisao = await this.aguardarDecisaoAnexoNoSalvar()
                        if (decisao === 'vinculado') {
                            anexado = await this.tentarAnexarPdfReuniao(arquivoPdf)
                        }
                    }

                    this.baixarBlob(relatorio.blob, relatorio.filename)
                    if (anexado) {
                        this.toast.success('Alterações salvas. Relatório PDF gerado e anexado na reunião.')
                    } else {
                        this.toast.success('Alterações salvas. Relatório PDF gerado.')
                    }
                } else {
                    this.toast.success('Alterações salvas com sucesso.')
                }
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.salvando = false
            }
        },

        abrirEntrega(entrega) {
            this.$router.push({
                name: 'projetoView',
                params: { idProjeto: entrega.projeto_id },
                query: { aba: 3, entrega_id: entrega.entrega_id },
            })
        },
    },
}
</script>
<style scoped>
.titulo-gestao-entregas {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.titulo-gestao-entregas h2 {
    margin: 0;
}

.gestao-entregas-corpo {
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
    padding-bottom: 5rem;
}

.toggle-estrategicos {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.85rem !important;
    border-radius: 999px !important;
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
}

.toggle-estrategicos--ativo {
    color: var(--cor-primaria-forte, #008a88) !important;
    border-color: var(--cor-primaria, #00b6b3) !important;
    background: var(--cor-primaria-fraca, rgba(0, 182, 179, 0.15)) !important;
}

.filtros-periodo {
    margin-bottom: 1.25rem;
}

.navegacao-semana {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.periodo-central {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    min-width: min(100%, 22rem);
}

.periodo-titulo {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    text-align: center;
}

.periodo-datas {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    justify-content: center;
    gap: 0.65rem;
}

.periodo-campo {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.periodo-campo label {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
}

.periodo-campo input[type="date"] {
    width: auto !important;
    min-width: 9.5rem;
}

.btn-setas,
.btn-hoje-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem !important;
    height: 2.5rem !important;
    min-width: 2.5rem;
    padding: 0 !important;
    border-radius: 8px !important;
    font-size: 1.15rem;
}

.btn-hoje-icon--ativo {
    color: var(--cor-primaria, #00b6b3) !important;
    border-color: var(--cor-primaria, #00b6b3) !important;
    background: var(--cor-primaria-fraca, rgba(0, 182, 179, 0.12)) !important;
}

.filtros-periodo-hint {
    margin: 0.75rem 0 0;
    font-size: 0.88rem;
    opacity: 0.75;
    text-align: center;
}

.acoes-reuniao-google {
    margin-top: 0.9rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.reuniao-vinculada-painel {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    max-width: 100%;
}

.chip-vinculo-reuniao {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: min(28rem, 100%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.btn-reuniao-acao {
    padding: 0.35rem 0.65rem !important;
    font-size: 0.82rem !important;
}

.btn-reuniao-excluir {
    color: #c62828 !important;
    border-color: rgba(198, 40, 40, 0.35) !important;
}

.secao-painel {
    margin-bottom: 2rem;
    max-width: 100%;
}

.secao-cabecalho {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.65rem;
    margin-bottom: 1rem;
}

.secao-cabecalho-principal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.secao-cabecalho h3 {
    margin: 0;
}

.secao-descricao {
    margin: -0.5rem 0 1rem;
    font-size: 0.9rem;
    opacity: 0.8;
}

.legenda-estados {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.legenda-estados-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.55));
}

.estado-vazio {
    margin: 1.25rem 0;
}

.vazio {
    display: inline-block;
    padding: 0.75rem 1.25rem;
}

.rodape-acoes {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    background: var(--cor-bg, #fff);
    border-top: 1px solid var(--cor-cinza2, #e0e0e0);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}

.rodape-selecao {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.rodape-botoes {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
}

@media (max-width: 640px) {
    .rodape-acoes {
        flex-direction: column;
        align-items: stretch;
    }

    .rodape-botoes {
        justify-content: stretch;
    }

    .rodape-botoes button {
        flex: 1;
    }
}

.btn-modo-apresentacao {
    position: fixed;
    bottom: 1.25rem;
    right: 1.25rem;
    left: auto;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.55rem 1rem !important;
    border-radius: 999px !important;
    font-weight: 600;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    background-color: var(--cor-bg);
}
</style>
